import React, { useState } from 'react';
import { Alert, AlertsResponse } from '../../services/faultIntelligenceService';
import AlertDrilldownModal from './AlertDrilldownModal';

interface AlertDashboardProps {
  alertsData: AlertsResponse | null;
  loading?: boolean;
  onAcknowledge?: (alertId: string) => void;
  onResolve?: (alertId: string) => void;
  onFilterChange?: (filters: { type?: string; status?: string }) => void;
}

const AlertDashboard: React.FC<AlertDashboardProps> = ({
  alertsData,
  loading,
  onAcknowledge,
  onResolve,
  onFilterChange
}) => {
  const [selectedType, setSelectedType] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('Active');
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleFilterChange = (type?: string, status?: string) => {
    const newType = type !== undefined ? type : selectedType;
    const newStatus = status !== undefined ? status : selectedStatus;
    
    setSelectedType(newType);
    setSelectedStatus(newStatus);
    onFilterChange?.({ type: newType || undefined, status: newStatus || undefined });
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'Critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'High': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Low': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return 'bg-red-100 text-red-800';
      case 'Acknowledged': return 'bg-yellow-100 text-yellow-800';
      case 'Resolved': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex gap-4 mb-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="ai-skeleton h-10 w-32"></div>
          ))}
        </div>
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="ai-skeleton h-16 w-full"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Alert Summary */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border">
          <div className="text-sm text-gray-600 dark:text-gray-400">Total Alerts</div>
          <div className="text-2xl font-bold text-red-600">{alertsData?.total || 0}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border">
          <div className="text-sm text-gray-600 dark:text-gray-400">Communication</div>
          <div className="text-2xl font-bold text-orange-600">{alertsData?.counts?.Communication || 0}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border">
          <div className="text-sm text-gray-600 dark:text-gray-400">Performance</div>
          <div className="text-2xl font-bold text-yellow-600">{alertsData?.counts?.Performance || 0}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border">
          <div className="text-sm text-gray-600 dark:text-gray-400">Grid</div>
          <div className="text-2xl font-bold text-blue-600">{alertsData?.counts?.Grid || 0}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-4">
        <select
          value={selectedType}
          onChange={(e) => handleFilterChange(e.target.value)}
          className="px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600"
        >
          <option value="">All Types</option>
          <option value="Communication">Communication</option>
          <option value="Performance">Performance</option>
          <option value="Grid">Grid</option>
          <option value="Equipment">Equipment</option>
          <option value="Weather">Weather</option>
        </select>
        
        <select
          value={selectedStatus}
          onChange={(e) => handleFilterChange(undefined, e.target.value)}
          className="px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600"
        >
          <option value="">All Status</option>
          <option value="Active">Active</option>
          <option value="Acknowledged">Acknowledged</option>
          <option value="Resolved">Resolved</option>
        </select>
      </div>

      {/* Alerts List */}
      <div className="space-y-2">
        {alertsData?.alerts?.map((alert) => (
          <div key={alert.alert_id} className="bg-white dark:bg-gray-800 p-4 rounded-lg border shadow-sm">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(alert.severity)}`}>
                    {alert.severity}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(alert.status)}`}>
                    {alert.status}
                  </span>
                  <span className="text-xs text-gray-500">{alert.type}</span>
                </div>
                
                <h4 
                  className="font-semibold text-gray-900 dark:text-white mb-1 cursor-pointer hover:text-blue-600"
                  onClick={() => {
                    setSelectedAlert(alert);
                    setIsModalOpen(true);
                  }}
                >
                  {alert.title}
                </h4>
                
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  {alert.message}
                </p>
                
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span>Equipment: {alert.equipment_id || 'N/A'}</span>
                  <span>Count: {alert.count}</span>
                  <span>Duration: {alert.duration}min</span>
                  <span>First seen: {new Date(alert.first_seen).toLocaleString()}</span>
                </div>
              </div>
              
              {alert.status === 'Active' && (
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => onAcknowledge?.(alert.alert_id)}
                    className="px-3 py-1 text-xs bg-yellow-100 text-yellow-800 rounded hover:bg-yellow-200"
                  >
                    Acknowledge
                  </button>
                  <button
                    onClick={() => onResolve?.(alert.alert_id)}
                    className="px-3 py-1 text-xs bg-green-100 text-green-800 rounded hover:bg-green-200"
                  >
                    Resolve
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
        
        {(!alertsData?.alerts || alertsData.alerts.length === 0) && (
          <div className="text-center py-8 text-gray-500">
            No alerts found
          </div>
        )}
      </div>

      <AlertDrilldownModal
        alert={selectedAlert}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedAlert(null);
        }}
        onAcknowledge={(alertId) => {
          onAcknowledge?.(alertId);
          setIsModalOpen(false);
          setSelectedAlert(null);
        }}
        onResolve={(alertId) => {
          onResolve?.(alertId);
          setIsModalOpen(false);
          setSelectedAlert(null);
        }}
      />
    </div>
  );
};

export default AlertDashboard;